// FloaterView.jsx
// Pure presentational floater. No Next.js, no context: used by both the
// website (via FloatingWidget.jsx) and the admin editor's live preview.
//
// Props
//   config        raw or resolved floatingWidget config
//   channels      channels to show (already filtered by the caller)
//   lang, defaultLocale
//   preview       true = position inside nearest relative container, no sessionStorage
//   getVars()     returns prefill variables at click time ({url, title, ...})
//   targets       per-channel target overrides ({ whatsapp: '66…' })
//   onItemClick   (channel) => void, e.g. analytics
//   closeKey      changing this closes the menu (e.g. pathname)
//   container     CSS container name: breakpoint rules use @container instead of
//                 @media (the editor preview canvas is container "hp")
'use client'

import { useEffect, useId, useMemo, useState } from 'react'
import { FloaterIcon } from './FloaterIcons'
import { CHANNELS, resolveConfig, pickLocale, buildChannelHref } from './floaterUtils'

const GREET_KEY = 'fw-greet-dismissed'

export default function FloaterView({
  config,
  channels,
  lang = 'en',
  defaultLocale = 'en',
  preview = false,
  getVars = () => ({}),
  targets = {},
  onItemClick,
  closeKey,
  container,
}) {
  const cfg = useMemo(() => resolveConfig(config), [config])
  const list = channels ?? cfg.channels
  const uid = useId().replace(/:/g, '')

  const [open, setOpen] = useState(false)
  const [qr, setQr] = useState(null)
  const [greet, setGreet] = useState(false)

  useEffect(() => setOpen(false), [closeKey])

  // Escape closes menu / QR
  useEffect(() => {
    if (!open && !qr) return
    const onKey = e => { if (e.key === 'Escape') { setOpen(false); setQr(null) } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, qr])

  // Greeting bubble
  const greetText = pickLocale(cfg.greeting.text, lang, defaultLocale)
  useEffect(() => {
    setGreet(false)
    if (!cfg.greeting.enabled || !greetText) return
    if (!preview && cfg.greeting.showOnce) {
      try { if (sessionStorage.getItem(GREET_KEY)) return } catch {}
    }
    const t = setTimeout(() => setGreet(true), preview ? 0 : cfg.greeting.delayMs)
    return () => clearTimeout(t)
  }, [cfg.greeting.enabled, cfg.greeting.delayMs, cfg.greeting.showOnce, greetText, preview])

  const dismissGreet = () => {
    setGreet(false)
    if (!preview) try { sessionStorage.setItem(GREET_KEY, '1') } catch {}
  }

  const css = useMemo(() => buildCss(cfg, uid, preview, container), [cfg, uid, preview, container])

  if (!list.length) return null

  const isMenu = cfg.layout === 'menu' || (cfg.layout === 'auto' && list.length > 1)
  const hrefFor = c => buildChannelHref(c, { lang, defaultLocale, vars: getVars(), targets })

  const handleClick = (e, c) => {
    onItemClick?.(c)
    if (c.type === 'wechat') { e.preventDefault(); setQr(c); return }
    if (preview) { e.preventDefault(); return }
    const href = hrefFor(c)   // recompute so {title}/{url} are current
    if (href) e.currentTarget.href = href
    setOpen(false)
  }

  const a = cfg.appearance
  const v = cfg.visibility
  const rootCls = ['fw', !v.desktop && 'fw-hd', !v.mobile && 'fw-hm'].filter(Boolean).join(' ')
  const itemLabelMode = isMenu && cfg.labelMode === 'hover' ? 'always' : cfg.labelMode

  const renderItem = c => {
    const def = CHANNELS[c.type] ?? CHANNELS.custom
    const label = pickLocale(c.label, lang, defaultLocale) || def.name
    const deviceCls = [!c.devices?.desktop && 'fw-hd', !c.devices?.mobile && 'fw-hm'].filter(Boolean).join(' ')
    const newTab = !def.sameTab && c.type !== 'wechat'
    return (
      <div key={c.id} className={`fw-row ${deviceCls}`}>
        {itemLabelMode !== 'none' && (
          <span className={`fw-label ${itemLabelMode === 'hover' ? 'fw-label-hover' : ''}`} aria-hidden="true">
            {label}
          </span>
        )}
        <a
          className={`fw-btn ${a.pulse && !isMenu ? 'fw-pulse' : ''}`}
          href={hrefFor(c) ?? '#'}
          target={newTab ? '_blank' : undefined}
          rel={newTab ? 'noopener noreferrer' : undefined}
          aria-label={label}
          style={{ background: c.bgColor || def.color, color: c.iconColor }}
          onClick={e => handleClick(e, c)}
        >
          <FloaterIcon type={c.type} url={c.iconUrl} color={c.iconColor} />
        </a>
      </div>
    )
  }

  const mb = cfg.mainButton
  const mainLabel = pickLocale(mb.label, lang, defaultLocale)
  const mainDeviceCls = [
    !list.some(c => c.devices?.desktop) && 'fw-hd',
    !list.some(c => c.devices?.mobile) && 'fw-hm',
  ].filter(Boolean).join(' ')
  const listId = `fw-list-${uid}`

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div data-fw={uid} className={rootCls}>
        {greet && !open && (
          <div className="fw-greet" role="status" onClick={() => { dismissGreet(); if (isMenu) setOpen(true) }}>
            {greetText}
            <button type="button" className="fw-greet-x" aria-label="Close"
              onClick={e => { e.stopPropagation(); dismissGreet() }}>
              <FloaterIcon type="close" />
            </button>
          </div>
        )}

        {isMenu ? (
          <>
            <div id={listId} className="fw-list" data-open={open}>
              {list.map(renderItem)}
            </div>
            <div className={`fw-row ${mainDeviceCls}`}>
              {mainLabel && cfg.labelMode !== 'none' && !open && (
                <span className={`fw-label ${cfg.labelMode === 'hover' ? 'fw-label-hover' : ''}`} aria-hidden="true">
                  {mainLabel}
                </span>
              )}
              <button
                type="button"
                className={`fw-btn ${a.pulse && !open ? 'fw-pulse' : ''}`}
                aria-expanded={open}
                aria-controls={listId}
                aria-label={mainLabel || 'Contact us'}
                style={{ background: mb.bgColor, color: mb.iconColor }}
                onClick={() => { setOpen(o => !o); dismissGreet() }}
              >
                {open
                  ? <FloaterIcon type="close" color={mb.iconColor} />
                  : <FloaterIcon type={mb.icon} url={mb.iconUrl} color={mb.iconColor} />}
              </button>
            </div>
          </>
        ) : (
          list.map(renderItem)
        )}
      </div>

      {qr && (
        <div data-fw={uid} className="fw-qr-bg" role="dialog" aria-modal="true"
          aria-label={pickLocale(qr.label, lang, defaultLocale) || 'WeChat'}
          onClick={() => setQr(null)}>
          <div className="fw-qr" onClick={e => e.stopPropagation()}>
            <button type="button" className="fw-qr-x" aria-label="Close" onClick={() => setQr(null)}>
              <FloaterIcon type="close" />
            </button>
            {qr.qrImage && <img src={qr.qrImage} alt="WeChat QR code" />}
            {qr.target && <p>WeChat ID: <strong>{qr.target}</strong></p>}
          </div>
        </div>
      )}
    </>
  )
}

// ─── Scoped CSS ───────────────────────────────────────────────────────────────

function buildCss(cfg, uid, preview, container) {
  const a = cfg.appearance
  const bp = Number(cfg.visibility.breakpoint) || 768
  const S = `[data-fw="${uid}"]`
  const side = a.position === 'left' ? 'left' : 'right'
  const pos = preview ? 'absolute' : 'fixed'
  const bpRule = container ? `@container ${container}` : '@media'

  return `
${S}.fw{position:${pos};z-index:${a.zIndex};${side}:${a.offsetX}px;bottom:calc(${a.offsetY}px + env(safe-area-inset-bottom,0px));display:flex;flex-direction:column;align-items:${side === 'left' ? 'flex-start' : 'flex-end'};gap:${a.gap}px;--fw-size:${a.size}px;--fw-icon:${a.iconSize}px}
${S} .fw-row{display:flex;align-items:center;gap:10px;flex-direction:${side === 'left' ? 'row-reverse' : 'row'}}
${S} .fw-btn{position:relative;isolation:isolate;width:var(--fw-size);height:var(--fw-size);border-radius:${a.radius};display:inline-flex;align-items:center;justify-content:center;border:0;padding:0;cursor:pointer;box-shadow:${a.shadow};text-decoration:none;transition:transform .15s ease}
${S} .fw-btn:hover{transform:scale(1.06)}
${S} .fw-btn:focus-visible{outline:3px solid #fff;outline-offset:2px;box-shadow:0 0 0 6px rgba(0,0,0,.35)}
${S} .fw-icon{width:var(--fw-icon);height:var(--fw-icon)}
${S} .fw-label{background:${a.labelBg};color:${a.labelColor};font-size:${a.labelFontSize};line-height:1.2;padding:6px 10px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,.12);white-space:nowrap}
${S} .fw-label-hover{opacity:0;pointer-events:none;transition:opacity .15s}
${S} .fw-row:hover .fw-label-hover,${S} .fw-row:focus-within .fw-label-hover{opacity:1}
@media (hover:none){${S} .fw-label-hover{display:none}}
${S} .fw-list{display:flex;flex-direction:column;align-items:inherit;gap:${a.gap}px;transition:opacity .18s,transform .18s,visibility .18s}
${S} .fw-list[data-open="false"]{opacity:0;transform:translateY(10px);visibility:hidden;pointer-events:none}
${S} .fw-pulse::after{content:"";position:absolute;inset:0;border-radius:inherit;background:inherit;z-index:-1;animation:fw-pulse 2s ease-out infinite}
@keyframes fw-pulse{0%{transform:scale(1);opacity:.55}100%{transform:scale(1.6);opacity:0}}
${S} .fw-greet{position:relative;max-width:260px;background:${a.labelBg};color:${a.labelColor};font-size:${a.labelFontSize};line-height:1.4;padding:12px 34px 12px 14px;border-radius:12px;box-shadow:0 6px 20px rgba(0,0,0,.15);cursor:pointer;animation:fw-in .25s ease-out}
${S} .fw-greet-x{position:absolute;top:6px;right:6px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;border:0;background:transparent;color:inherit;opacity:.6;cursor:pointer}
${S} .fw-greet-x .fw-icon{width:12px;height:12px}
@keyframes fw-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
${S}.fw-qr-bg{position:${pos};inset:0;z-index:${a.zIndex + 1};background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px}
${S} .fw-qr{position:relative;background:#fff;color:#111;border-radius:12px;padding:24px;max-width:320px;width:100%;text-align:center}
${S} .fw-qr img{width:100%;height:auto;display:block;margin-bottom:12px}
${S} .fw-qr-x{position:absolute;top:8px;right:8px;border:0;background:transparent;cursor:pointer;width:28px;height:28px;display:flex;align-items:center;justify-content:center}
${S} .fw-qr-x .fw-icon{width:14px;height:14px}
${bpRule} (max-width:${bp - 0.02}px){
  ${S}.fw{${side}:${a.mobileOffsetX}px;bottom:calc(${a.mobileOffsetY}px + env(safe-area-inset-bottom,0px));--fw-size:${a.mobileSize}px;--fw-icon:${a.mobileIconSize}px}
  ${S}.fw-hm,${S} .fw-hm{display:none!important}
}
${bpRule} (min-width:${bp}px){${S}.fw-hd,${S} .fw-hd{display:none!important}}
@media (prefers-reduced-motion:reduce){${S} *,${S} *::after{animation:none!important;transition:none!important}}
@media print{${S}.fw{display:none!important}}
`
}
